import hark from "hark";
import { useEffect, useRef } from "react";

import { recordErrorInSentry } from "../../utils/init-sentry.js";
import { renderedAvatarUserIdsStore } from "./rendered-avatar-user-ids.store.js";
import { resolveSelfSpeakingUserId } from "./resolve-speaking-user-id.js";
import { speakingStore } from "./speaking-state.js";
import type {
  RemoteAudioProjection,
  TableCallParticipantDirectory,
} from "./table-call-participant-directory.js";
import type { MeetingClient } from "./table-call.context.js";
import { AUDIO_RECOVERY_DELAY_MS } from "./table-call.provider.constants.js";

const VOLUME_SAMPLES_PER_SECOND = 20;
const DEFAULT_VOLUME_THRESHOLD = 0.3;
const MS_PER_SECOND = 1000;
const DB_FLOOR = -100;
const DB_CEILING = -10;
const RESOLVED_USER_ID_MISMATCH_RECHECK_MS = 200;
const STALE_VOLUME_MULTIPLIER = 3;
const STALE_VOLUME_TIMEOUT_MS =
  (MS_PER_SECOND / VOLUME_SAMPLES_PER_SECOND) * STALE_VOLUME_MULTIPLIER;

interface AudioUpdatePayload {
  audioEnabled: boolean;
  audioTrack: MediaStreamTrack | null | undefined;
}

interface TrackableParticipant {
  id: string;
  audioEnabled: boolean;
  audioTrack?: MediaStreamTrack | null;
  source: unknown;
  subscribeToAudioUpdates?: (
    listener: (payload: AudioUpdatePayload) => void
  ) => () => void;
}

interface ParticipantTrackerEntry {
  participantSource: unknown;
  cleanup: (options?: { keepState?: boolean }) => void;
}

const toAudioParticipant = ({
  remote,
  track,
}: RemoteAudioProjection): TrackableParticipant => ({
  audioEnabled: remote.audioEnabled,
  audioTrack: track,
  id: remote.peerId,
  source: remote,
});

const toSelfAudioParticipant = (
  participant: MeetingClient["self"]
): TrackableParticipant => ({
  audioEnabled: participant.audioEnabled,
  audioTrack: participant.audioTrack,
  id: participant.id,
  source: participant,
  subscribeToAudioUpdates: (listener) => {
    participant.addListener("audioUpdate", listener);
    return () => participant.removeListener("audioUpdate", listener);
  },
});

const getAudioTrackIdentity = (
  audioTrack: MediaStreamTrack | null | undefined
) => {
  if (!audioTrack) {
    return null;
  }

  const deviceId = audioTrack.getSettings().deviceId ?? "";
  return `${audioTrack.id}:${deviceId}`;
};

const createParticipantSpeakingTracker = ({
  userId,
  participant,
  volumeThreshold,
  canReportSpeaking,
}: {
  userId: string;
  participant: TrackableParticipant;
  volumeThreshold: number;
  canReportSpeaking?: () => boolean;
}): Pick<ParticipantTrackerEntry, "cleanup"> => {
  let isDisposed = false;
  let isAudioEnabled = false;
  let currentVolumeLevel = 0;
  let isHarkSpeaking = false;
  let currentSpeaking = false;
  let harkInstance: ReturnType<typeof hark> | null = null;
  let audioUpdateVersion = 0;
  let lastAudioEnabled: boolean | null = null;
  let lastAudioTrackIdentity: string | null = null;
  let currentAudioTrack: MediaStreamTrack | null = null;
  let volumeWatchdogTimeout: ReturnType<typeof setTimeout> | null = null;
  let speakingSuppressedUntil = 0;
  const shouldSuppressTrackTransitions = Boolean(canReportSpeaking);

  const setSpeakingState = (
    nextIsSpeaking: boolean,
    nextVolumeLevel: number
  ) => {
    const normalizedVolumeLevel = nextIsSpeaking ? nextVolumeLevel : 0;
    if (
      currentSpeaking === nextIsSpeaking &&
      currentVolumeLevel === normalizedVolumeLevel
    ) {
      return;
    }

    currentSpeaking = nextIsSpeaking;
    currentVolumeLevel = normalizedVolumeLevel;
    speakingStore.setSpeakingState(userId, {
      isSpeaking: currentSpeaking,
      volumeLevel: currentVolumeLevel,
    });
  };

  const syncSpeakingState = (nextVolumeLevel?: number) => {
    // RTK's `participants.active` tracks layout/media subscription state, not
    // per-user voice activity, so speaking state must come from track analysis.
    if (Date.now() < speakingSuppressedUntil) {
      setSpeakingState(false, 0);
      return;
    }

    const nextIsSpeaking =
      (canReportSpeaking?.() ?? true) && isAudioEnabled && isHarkSpeaking;
    setSpeakingState(nextIsSpeaking, nextVolumeLevel ?? currentVolumeLevel);
  };

  const clearVolumeWatchdog = () => {
    if (!volumeWatchdogTimeout) {
      return;
    }

    clearTimeout(volumeWatchdogTimeout);
    volumeWatchdogTimeout = null;
  };

  const scheduleVolumeWatchdog = (version: number) => {
    clearVolumeWatchdog();
    volumeWatchdogTimeout = setTimeout(() => {
      volumeWatchdogTimeout = null;

      if (isDisposed || version !== audioUpdateVersion) {
        return;
      }

      if (!(isAudioEnabled && currentAudioTrack)) {
        return;
      }

      if (currentAudioTrack.readyState !== "live") {
        return;
      }

      isHarkSpeaking = false;
      currentVolumeLevel = 0;
      setSpeakingState(false, 0);
    }, STALE_VOLUME_TIMEOUT_MS);
  };

  const resetHark = () => {
    clearVolumeWatchdog();
    harkInstance?.stop();
    harkInstance = null;
  };

  const onAudioTrackEnded = () => {
    if (isDisposed) {
      return;
    }

    if (shouldSuppressTrackTransitions) {
      speakingSuppressedUntil = Date.now() + AUDIO_RECOVERY_DELAY_MS;
    }

    // Force the next audioUpdate to be processed even if RTK reports the same
    // track reference, because the track has already transitioned to `ended`.
    lastAudioEnabled = null;
    lastAudioTrackIdentity = null;
    audioUpdateVersion += 1;
    isAudioEnabled = false;
    isHarkSpeaking = false;
    currentVolumeLevel = 0;
    resetHark();
    syncSpeakingState(0);
  };

  const suppressSpeakingStateIfNeeded = () => {
    if (shouldSuppressTrackTransitions) {
      speakingSuppressedUntil = Date.now() + AUDIO_RECOVERY_DELAY_MS;
    }
  };

  const setInactiveSpeakingState = () => {
    currentVolumeLevel = 0;
    syncSpeakingState(0);
  };

  const handleInactiveAudioTrack = () => {
    suppressSpeakingStateIfNeeded();
    setInactiveSpeakingState();
  };

  const attachLiveAudioTrack = (
    audioTrack: MediaStreamTrack,
    currentAudioUpdateVersion: number
  ) => {
    currentAudioTrack = audioTrack;
    currentAudioTrack.addEventListener("ended", onAudioTrackEnded);

    currentVolumeLevel = 0;
    setSpeakingState(false, 0);

    try {
      const stream = new MediaStream();
      stream.addTrack(audioTrack);
      harkInstance = hark(stream, {
        interval: MS_PER_SECOND / VOLUME_SAMPLES_PER_SECOND,
        play: false,
      });

      scheduleVolumeWatchdog(currentAudioUpdateVersion);

      harkInstance.on("volume_change", (dBs: number) => {
        if (isDisposed || currentAudioUpdateVersion !== audioUpdateVersion) {
          return;
        }

        const clamped = Math.max(DB_FLOOR, Math.min(DB_CEILING, dBs));
        const rawVolume = (clamped - DB_FLOOR) / (DB_CEILING - DB_FLOOR);
        isHarkSpeaking = rawVolume >= volumeThreshold;
        currentVolumeLevel = rawVolume;
        syncSpeakingState(rawVolume);
        scheduleVolumeWatchdog(currentAudioUpdateVersion);
      });
    } catch (error) {
      console.error("TableCall speaking tracker failed to initialize hark", {
        audioTrackReadyState: audioTrack.readyState,
        error,
        participantId: participant.id,
        userId,
      });
      harkInstance = null;
      currentVolumeLevel = 0;
      syncSpeakingState(0);
    }
  };

  const clearTrackEndedListener = () => {
    if (!currentAudioTrack) {
      return;
    }
    currentAudioTrack.removeEventListener("ended", onAudioTrackEnded);
    currentAudioTrack = null;
  };

  const onAudioUpdate = ({ audioEnabled, audioTrack }: AudioUpdatePayload) => {
    const nextAudioTrackIdentity = getAudioTrackIdentity(audioTrack);
    if (
      audioEnabled === lastAudioEnabled &&
      nextAudioTrackIdentity === lastAudioTrackIdentity
    ) {
      return;
    }

    lastAudioEnabled = audioEnabled;
    lastAudioTrackIdentity = nextAudioTrackIdentity;

    clearTrackEndedListener();
    audioUpdateVersion += 1;
    const currentAudioUpdateVersion = audioUpdateVersion;
    isAudioEnabled = audioEnabled;
    resetHark();
    isHarkSpeaking = false;

    if (!(audioEnabled && audioTrack)) {
      handleInactiveAudioTrack();
      return;
    }

    if (audioTrack.readyState !== "live") {
      handleInactiveAudioTrack();
      return;
    }

    attachLiveAudioTrack(audioTrack, currentAudioUpdateVersion);
  };

  const unsubscribeFromAudioUpdates =
    participant.subscribeToAudioUpdates?.(onAudioUpdate);
  onAudioUpdate({
    audioEnabled: participant.audioEnabled,
    audioTrack: participant.audioTrack,
  });

  return {
    cleanup: ({ keepState } = {}) => {
      isDisposed = true;
      clearTrackEndedListener();
      resetHark();
      unsubscribeFromAudioUpdates?.();

      if (!keepState) {
        speakingStore.remove(userId);
      }
    },
  };
};

export const useTableCallSpeakingTracker = ({
  meeting,
  participantDirectory,
  selfUserId,
  selfMicrophoneActive,
  tableId,
  volumeThreshold = DEFAULT_VOLUME_THRESHOLD,
}: {
  meeting: MeetingClient | null;
  participantDirectory: TableCallParticipantDirectory;
  selfUserId: string;
  selfMicrophoneActive: boolean;
  tableId: string | null;
  volumeThreshold?: number;
}) => {
  const selfCanReportSpeakingRef = useRef(selfMicrophoneActive);

  useEffect(() => {
    selfCanReportSpeakingRef.current = selfMicrophoneActive;
  }, [selfMicrophoneActive]);

  useEffect(() => {
    const currentCallTableId = tableId ?? "no-table";
    speakingStore.reset();

    if (!meeting) {
      return () => {
        speakingStore.clear();
      };
    }

    const trackersByUserId = new Map<string, ParticipantTrackerEntry>();
    const reportedMissingRenderedUserIdsByParticipantId = new Map<
      string,
      string
    >();
    const pendingResolvedUserIdMismatchTimeoutsByParticipantId = new Map<
      string,
      ReturnType<typeof setTimeout>
    >();

    const clearPendingResolvedUserIdMismatch = (participantId: string) => {
      const timeoutId =
        pendingResolvedUserIdMismatchTimeoutsByParticipantId.get(participantId);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      pendingResolvedUserIdMismatchTimeoutsByParticipantId.delete(
        participantId
      );
    };

    // Retraction for R2: when an off-view / virtualized avatar mounts after a
    // mismatch was already reported, drop the dedupe entry so the state is
    // clean again (and a later legitimate absence can be re-evaluated).
    const clearStaleResolvedUserIdMismatchReports = () => {
      for (const [
        participantId,
        userId,
      ] of reportedMissingRenderedUserIdsByParticipantId) {
        if (renderedAvatarUserIdsStore.has(userId)) {
          reportedMissingRenderedUserIdsByParticipantId.delete(participantId);
        }
      }
    };

    // Reports the P3 contract violation: a resolved remote speaking key
    // (`customParticipantId`) that corresponds to no rendered avatar
    // (`user.id`), which means the speaking ring would silently never show.
    // Reported to Sentry in every environment.
    const reportResolvedUserIdMismatch = ({
      participant,
      userId,
    }: {
      participant: TrackableParticipant;
      userId: string;
    }) => {
      recordErrorInSentry(
        new Error("Resolved speaking user id matches no rendered avatar"),
        {
          extra: {
            participantId: participant.id,
            renderedAvatarUserIds: renderedAvatarUserIdsStore.getAll(),
            resolvedUserId: userId,
            tableId: currentCallTableId,
          },
          tags: {
            event: "speaking-user-id-mismatch",
            feature: "table-call",
          },
        }
      );
    };

    const upsertTracker = (
      userId: string,
      participant: TrackableParticipant
    ) => {
      const existing = trackersByUserId.get(userId);
      if (existing?.participantSource === participant.source) {
        return;
      }

      existing?.cleanup();

      const tracker = createParticipantSpeakingTracker({
        participant,
        userId,
        volumeThreshold,
        ...(userId === selfUserId
          ? {
              canReportSpeaking: () => selfCanReportSpeakingRef.current,
            }
          : {}),
      });
      trackersByUserId.set(userId, {
        cleanup: tracker.cleanup,
        participantSource: participant.source,
      });
    };

    const maybeRecordResolvedUserIdMismatch = ({
      participant,
      userId,
    }: {
      participant: TrackableParticipant;
      userId: string | null;
    }) => {
      if (!userId) {
        clearPendingResolvedUserIdMismatch(participant.id);
        reportedMissingRenderedUserIdsByParticipantId.delete(participant.id);
        return;
      }

      if (renderedAvatarUserIdsStore.has(userId)) {
        clearPendingResolvedUserIdMismatch(participant.id);
        reportedMissingRenderedUserIdsByParticipantId.delete(participant.id);
        return;
      }

      const currentReportedUserId =
        reportedMissingRenderedUserIdsByParticipantId.get(participant.id);
      if (currentReportedUserId === userId) {
        return;
      }

      clearPendingResolvedUserIdMismatch(participant.id);
      const timeoutId = setTimeout(() => {
        pendingResolvedUserIdMismatchTimeoutsByParticipantId.delete(
          participant.id
        );

        if (renderedAvatarUserIdsStore.has(userId)) {
          reportedMissingRenderedUserIdsByParticipantId.delete(participant.id);
          return;
        }

        reportedMissingRenderedUserIdsByParticipantId.set(
          participant.id,
          userId
        );
        reportResolvedUserIdMismatch({
          participant,
          userId,
        });
      }, RESOLVED_USER_ID_MISMATCH_RECHECK_MS);

      pendingResolvedUserIdMismatchTimeoutsByParticipantId.set(
        participant.id,
        timeoutId
      );
    };

    const buildNextParticipants = () => {
      const nextParticipantsByUserId = new Map<string, TrackableParticipant>();
      const selfParticipant = toSelfAudioParticipant(meeting.self);

      const selfSpeakingUserId = resolveSelfSpeakingUserId(selfUserId);
      if (selfSpeakingUserId) {
        nextParticipantsByUserId.set(selfSpeakingUserId, selfParticipant);
      }

      for (const projection of participantDirectory.getRemoteAudio()) {
        const { remote } = projection;
        const participantUserId = remote.participantId;
        const participant = toAudioParticipant(projection);
        maybeRecordResolvedUserIdMismatch({
          participant,
          userId: participantUserId,
        });
        nextParticipantsByUserId.set(participantUserId, participant);
      }

      return nextParticipantsByUserId;
    };

    const reconcileTrackers = (
      nextParticipantsByUserId: Map<string, TrackableParticipant>
    ) => {
      for (const [userId, participant] of nextParticipantsByUserId) {
        upsertTracker(userId, participant);
      }

      for (const [userId, tracker] of trackersByUserId) {
        if (nextParticipantsByUserId.has(userId)) {
          continue;
        }
        tracker.cleanup();
        trackersByUserId.delete(userId);
      }
    };

    const syncTrackers = () => {
      const nextParticipantsByUserId = buildNextParticipants();

      reconcileTrackers(nextParticipantsByUserId);
    };

    const unsubscribeParticipantDirectory =
      participantDirectory.subscribeAll(syncTrackers);

    // Re-evaluate stale mismatch reports when the rendered-avatar set changes,
    // so a report for an avatar that mounts later (off-view / virtualized)
    // clears itself instead of lingering until the next participant event.
    const unsubscribeFromRenderedAvatars = renderedAvatarUserIdsStore.subscribe(
      clearStaleResolvedUserIdMismatchReports
    );

    syncTrackers();

    return () => {
      unsubscribeParticipantDirectory();
      unsubscribeFromRenderedAvatars();

      for (const tracker of trackersByUserId.values()) {
        tracker.cleanup({ keepState: true });
      }
      trackersByUserId.clear();
      for (const participantId of pendingResolvedUserIdMismatchTimeoutsByParticipantId.keys()) {
        clearPendingResolvedUserIdMismatch(participantId);
      }
      pendingResolvedUserIdMismatchTimeoutsByParticipantId.clear();
      reportedMissingRenderedUserIdsByParticipantId.clear();
      speakingStore.clear();
    };
  }, [meeting, participantDirectory, selfUserId, tableId, volumeThreshold]);
};
